import { useMemo } from "react";
import { isOptionSymbol, isIndexSymbol } from "./useStockOptionsView";

/**
 * Extract commodity option name from symbol
 * @param {string} symbol - e.g. "MCX:CRUDEOIL24OCT6500CE"
 * @returns {string|null} - e.g. "CRUDEOIL"
 */
export function getCommodityOptionFromSymbol(symbol) {
  try {
    if (!symbol) return null;
    const s = String(symbol).toUpperCase().trim();

    // Must be an option contract (ends strictly with CE or PE, excluding FUT)
    if (!isOptionSymbol(s)) return null;

    // Exclude Index options (SENSEX, NIFTY, BANKNIFTY, etc.)
    if (isIndexSymbol(s)) return null;

    const cleanSymbol = s.replace("MCX:", "").replace("NFO:", "").replace("BFO:", "").replace("NSE:", "").replace("BSE:", "");
    const commodities = [
      "CRUDEOIL", "GOLD", "SILVER", "NATURALGAS",
      "COPPER", "ZINC", "LEAD", "ALUMINIUM", "MENTHAOIL", "COTTON"
    ];

    for (const comm of commodities) {
      if (cleanSymbol.startsWith(comm)) return comm;
    }

    if (s.includes("MCX")) {
      const match = cleanSymbol.match(/^([A-Z]+)/);
      return match ? match[1] : null;
    }

    return null;
  } catch (e) {
    console.error("Error extracting commodity option from symbol:", symbol, e);
    return null;
  }
}

/**
 * Normalize side
 */
function normalizeSide(rawSide) {
  if (rawSide === null || rawSide === undefined) return null;
  const s = String(rawSide).trim().toLowerCase();
  if (s === "1" || s === "buy" || s === "b") return "buy";
  if (s === "2" || s === "-1" || s === "sell" || s === "s") return "sell";
  return null;
}

/**
 * Hook to view Commodity Options Positions
 */
export function useCommodityOptionsPositionsView(
  positions,
  orders,
  userMappings,
  positionsUpdateVersion
) {
  const commodityOptionsViewData = useMemo(() => {
    if (!positions || positions.length === 0) {
      return { data: [], baseQtyMap: {}, activeCommodities: [], symbolStrategyBaseQty: {} };
    }

    // Filter commodity options positions for client CA00246 only
    const commodityPositions = positions.filter((p) => {
      const clientId = p.client_id || p.clientId;
      if (clientId !== "CA00246") return false;
      const comm = getCommodityOptionFromSymbol(p.symbol);
      const netQty = parseFloat(p.net_qty ?? p.qty ?? 0);
      return comm !== null && Math.abs(netQty) > 0;
    });

    const commSet = new Set();
    commodityPositions.forEach((p) => {
      const comm = getCommodityOptionFromSymbol(p.symbol);
      if (comm) commSet.add(comm);
    });

    const activeCommodities = Array.from(commSet);

    // Group positions by client and commodity symbol
    const clientCommMap = {};

    commodityPositions.forEach((p) => {
      const clientId = p.client_id || p.clientId;
      const comm = getCommodityOptionFromSymbol(p.symbol);
      if (!clientId || !comm) return;

      if (!clientCommMap[clientId]) {
        clientCommMap[clientId] = {};
      }

      if (!clientCommMap[clientId][comm]) {
        clientCommMap[clientId][comm] = {
          qty: 0,
          slQty: 0,
          pnl: 0,
          positions: []
        };
      }

      const qty = Math.abs(parseFloat(p.net_qty ?? p.qty ?? 0));
      const pnl = parseFloat(p.pl ?? p.pnl ?? 0);

      clientCommMap[clientId][comm].qty += qty;
      clientCommMap[clientId][comm].pnl += pnl;
      clientCommMap[clientId][comm].positions.push(p);
    });

    // Process matching sell orders for SL Qty
    if (orders && orders.length > 0) {
      orders.forEach((o) => {
        if (normalizeSide(o.side) !== "sell") return;
        const clientId = o.clientId || o.client_id;
        if (clientId !== "CA00246") return;
        const comm = getCommodityOptionFromSymbol(o.symbol);
        if (!comm) return;

        if (!clientCommMap[clientId]) {
          clientCommMap[clientId] = {};
        }
        if (!clientCommMap[clientId][comm]) {
          clientCommMap[clientId][comm] = {
            qty: 0,
            slQty: 0,
            pnl: 0,
            positions: []
          };
        }
        clientCommMap[clientId][comm].slQty += Math.abs(parseFloat(o.quantity || o.qty || 0));
      });
    }

    const data = [];
    Object.keys(clientCommMap).forEach((clientId) => {
      let userMapping = null;
      if (userMappings) {
        const matches = userMappings.filter((u) => u.client_id === clientId);
        userMapping = matches.find((u) => (u.broker || "").toLowerCase() === "dhan_xts") || matches[0];
      }

      const rowData = {
        clientId,
        clientName: userMapping ? userMapping.name : `Client ${clientId}`,
        status: userMapping ? userMapping.status : "unknown",
        broker: userMapping ? userMapping.broker : "unknown",
        totalMultiplier: 1,
      };

      activeCommodities.forEach((comm) => {
        if (clientCommMap[clientId][comm]) {
          const commInfo = clientCommMap[clientId][comm];
          const qty = commInfo.qty;
          const slQty = commInfo.slQty;
          const pnl = commInfo.pnl;

          let color = "gray";
          if (qty > 0) {
            if (slQty === qty) color = "green";
            else if (slQty > 0 || slQty < qty) color = "red";
          }

          const commObj = {
            qty,
            slQty,
            pnl,
            color,
            multiplier: 1,
            positions: commInfo.positions
          };

          rowData[comm] = commObj;
          rowData[comm.toLowerCase()] = commObj;
          rowData[comm.toUpperCase()] = commObj;
        }
      });

      data.push(rowData);
    });

    const symbolStrategyBaseQty = {};
    commodityPositions.forEach((p) => {
      const comm = getCommodityOptionFromSymbol(p.symbol);
      if (!comm) return;
      const key = `${p.symbol}_${p.strategy || "NO_STRATEGY"}`;
      if (!symbolStrategyBaseQty[key]) {
        const netQty = Math.abs(parseFloat(p.net_qty ?? p.qty ?? 0));
        symbolStrategyBaseQty[key] = {
          symbol: p.symbol,
          strategy: p.strategy,
          index: comm,
          baseQty: netQty,
          clients: 1
        };
      }
    });

    return {
      data,
      baseQtyMap: {},
      activeCommodities,
      symbolStrategyBaseQty
    };
  }, [positions, orders, userMappings, positionsUpdateVersion]);

  return commodityOptionsViewData;
}

/**
 * Hook to view Commodity Options Orders
 */
export function useCommodityOptionsOrdersView(
  orders,
  userMappings,
  ordersUpdateVersion,
  users
) {
  const commodityOptionsOrderData = useMemo(() => {
    if (!orders || orders.length === 0) {
      return { data: [], activeCommodities: [], activeIndexes: [], buyOrdersCount: 0, totalOrdersCount: 0 };
    }

    // Filter commodity option orders for client CA00246 only
    const commOrders = orders.filter((o) => {
      const clientId = o.clientId || o.client_id;
      if (clientId !== "CA00246") return false;
      const comm = getCommodityOptionFromSymbol(o.symbol);
      return comm !== null;
    });

    const buyOrders = commOrders.filter((o) => {
      const side = String(o.side ?? "").trim().toLowerCase();
      return side === "1" || side === "buy" || side === "b";
    });

    // Group buy orders by client and commodity symbol
    const clientCommMap = {};

    buyOrders.forEach((o) => {
      const clientId = o.clientId || o.client_id;
      const comm = getCommodityOptionFromSymbol(o.symbol);
      if (!clientId || !comm) return;

      if (!clientCommMap[clientId]) {
        clientCommMap[clientId] = {};
      }

      if (!clientCommMap[clientId][comm]) {
        clientCommMap[clientId][comm] = {
          qty: 0,
          orders: []
        };
      }

      const qty = Math.abs(parseFloat(o.quantity || o.qty || 0));
      clientCommMap[clientId][comm].qty += qty;
      clientCommMap[clientId][comm].orders.push(o);
    });

    const activeCommodities = Array.from(
      new Set(
        buyOrders
          .map((o) => getCommodityOptionFromSymbol(o.symbol))
          .filter(Boolean)
      )
    );

    const data = [];
    Object.keys(clientCommMap).forEach((clientId) => {
      let userMapping = null;
      if (userMappings) {
        const matches = userMappings.filter((u) => u.client_id === clientId);
        userMapping = matches.find((u) => (u.broker || "").toLowerCase() === "dhan_xts") || matches[0];
      }

      const rowData = {
        clientId,
        clientName: userMapping ? userMapping.name : `Client ${clientId}`,
        status: userMapping ? userMapping.status : "unknown",
        broker: userMapping ? userMapping.broker : "unknown",
        totalMultiplier: 1,
      };

      activeCommodities.forEach((comm) => {
        if (clientCommMap[clientId][comm]) {
          const commInfo = clientCommMap[clientId][comm];
          const commObj = {
            qty: commInfo.qty,
            color: "green",
            multiplier: 1,
            orders: commInfo.orders
          };

          rowData[comm] = commObj;
          rowData[comm.toLowerCase()] = commObj;
          rowData[comm.toUpperCase()] = commObj;
        }
      });

      data.push(rowData);
    });

    const symbolStrategyBaseQty = {};
    buyOrders.forEach((o) => {
      const comm = getCommodityOptionFromSymbol(o.symbol);
      if (!comm) return;
      const key = `${o.symbol}_${o.strategy || "NO_STRATEGY"}`;
      if (!symbolStrategyBaseQty[key]) {
        const qty = Math.abs(parseFloat(o.quantity || o.qty || 0));
        symbolStrategyBaseQty[key] = {
          symbol: o.symbol,
          strategy: o.strategy,
          index: comm,
          baseQty: qty,
          clients: 1
        };
      }
    });

    return {
      data,
      activeCommodities,
      activeIndexes: activeCommodities,
      baseQtyMap: {},
      symbolStrategyBaseQty,
      buyOrdersCount: buyOrders.length,
      totalOrdersCount: commOrders.length
    };
  }, [orders, userMappings, ordersUpdateVersion, users]);

  return commodityOptionsOrderData;
}
